import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";
import toast from "react-hot-toast";
import { FiMail, FiSend, FiCheckCircle, FiMessageCircle } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { BsChatSquareDotsFill } from "react-icons/bs";
import Spinner from "../../components/common/Spinner";
import themeBg from "../../assests/theme.png";

const EMAILJS_SERVICE_ID  = "service_78twapj";
const EMAILJS_TEMPLATE_ID = "template_d41zvql";
const EMAILJS_PUBLIC_KEY  = "PTBrs8XGbNEfrSPbz";
  
export default function Contact() {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
 
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
 
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );
      setSent(true);
      reset();
      toast.success("Message sent! We'll reply soon.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };
 
 return (
    <div className="min-h-screen" style={{ background: "#f7f3e3" }}>

      <Helmet>
        <title>Contact Us | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-leaf-600 to-primary-700 py-20 px-4 text-center text-white">
        {/* subtle decorative rings */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -bottom-16 -right-16 w-72 h-72 rounded-full border border-white/10" />

        <span className="inline-block text-xs font-semibold tracking-widest uppercase bg-white/20 px-4 py-1.5 rounded-full mb-4">
          We're here to help
        </span>
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-4 leading-tight" style={{ fontFamily: "var(--custom-body-font-family)" }}>
          Contact Tripura Agro
        </h1>
        <p className="text-white/85 max-w-xl mx-auto text-base md:text-lg">
          Questions about bulk orders, delivery, or our products?
          Drop us a message and we'll get back to you within 24 hours.
        </p>
      </section>

    
      {/* ── Header ── */}
      <div className="text-center py-10 px-4" style={{ background: "#f7f3e3" }}>
        <h1
          className="font-bold tracking-widest uppercase mb-3"
          style={{
            fontSize: "clamp(1.4rem, 3vw, 2rem)",
            color: "#2d3a1e",
            letterSpacing: "0.18em",
          }}
        >
          HELP AND SUPPORT
        </h1>
        <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: "#666" }}>
          We're here to help you! Whether you have questions about our products, need assistance,
          or just want to share your thoughts, we'd love to hear from you.
        </p>
      </div>

      {/* ── Body: Form + Info ── */}
      <div className="relative overflow-hidden">
        {/* Decorative background illustration — sits behind the form (left side), low opacity so fields stay readable */}
        <img
          src={themeBg}
          alt=""
          aria-hidden="true"
          className="absolute top-0 left-0 w-[480px] sm:w-[640px] pointer-events-none select-none"
          style={{ opacity: 0.1, zIndex: 0 }}
        />

        <div
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 relative"
          style={{ display: "grid", gridTemplateColumns: "1fr", gap: "2rem", zIndex: 1 }}
        >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* ── Left: Form ── */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mb-6">
                  <FiCheckCircle className="w-9 h-9 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2" style={{ color: "#2d3a1e" }}>Message Sent!</h3>
                <p className="text-gray-500 max-w-xs mb-8">
                  Thank you for reaching out. We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="flex items-center gap-2 px-6 py-2 rounded font-semibold text-white"
                  style={{ background: "#4a6e30" }}
                >
                  <FiMessageCircle className="w-4 h-4" /> Send Another
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-1" style={{ color: "#2d3a1e" }}>Send us an email</h2>
                <p className="text-sm mb-6" style={{ color: "#888" }}>Ask us anything! We're here to help.</p>

                <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="space-y-4">

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: "#444" }}>Name</label>
                    <input
                      {...register("name", { required: "Name is required" })}
                      placeholder=""
                      className="w-full border px-3 py-2.5 text-sm outline-none focus:border-gray-400 transition"
                      style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: "#333",
                      }}
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: "#444" }}>Phone Number</label>
                    <input
                      {...register("mobile", { required: "Phone is required" })}
                      placeholder=""
                      className="w-full border px-3 py-2.5 text-sm outline-none focus:border-gray-400 transition"
                      style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: "#333",
                      }}
                    />
                    {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile.message}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: "#444" }}>
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("email", {
                        required: "Email is required",
                        pattern: { value: /^\S+@\S+$/, message: "Invalid email" },
                      })}
                      type="email"
                      placeholder=""
                      className="w-full border px-3 py-2.5 text-sm outline-none focus:border-gray-400 transition"
                      style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: "#333",
                      }}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
                  </div>

                  {/* Comment / Message */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: "#444" }}>
                      Comment <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      {...register("message", {
                        required: "Message is required",
                        minLength: { value: 10, message: "Minimum 10 characters" },
                      })}
                      name="message"
                      rows={7}
                      className="w-full border px-3 py-2.5 text-sm outline-none focus:border-gray-400 transition resize-y"
                      style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        color: "#333",
                      }}
                    />
                    {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message.message}</p>}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center bg-gradient-to-br from-leaf-600 to-primary-700 gap-2 px-8 py-3 font-bold text-sm tracking-widest uppercase text-white transition hover:opacity-90"
                    style={{
                      borderRadius: "4px",
                      minWidth: "200px",
                    }}
                  >
                    {loading
                      ? <><Spinner size="sm" /> Sending...</>
                      : "SUBMIT CONTACT"}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* ── Right: Info Panel ── */}
          <div className="lg:col-span-2">
            <div
              className="p-6 sm:p-8"
              style={{
                background: "#fff",
                border: "1px solid #e8e4d8",
                borderRadius: "6px",
              }}
            >
              <h3 className="text-lg font-bold mb-2" style={{ color: "#2d3a1e" }}>
                We're Just a Message Away
              </h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: "#666" }}>
                We're here to help! Whether you have a question about our services, want to collaborate,
                or simply want to say hello, we'd love to hear from you. Use the form below to send us
                a message, and we'll get back to you as soon as possible.
              </p>

              {/* Message Us button */}
              <a
                href="https://wa.me/919778553533"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full text-center font-bold tracking-widest uppercase py-3 mb-6 transition hover:bg-gray-50"
                style={{
                  border: "1.5px solid #333",
                  borderRadius: "4px",
                  color: "#222",
                  fontSize: "0.85rem",
                  letterSpacing: "0.12em",
                }}
              >
                MESSAGE US
              </a>

              {/* Contact lines */}
              <div className="space-y-3 mb-5">
                {[
                  { label: "Call:", value: "+91 97785 53533", href: "tel:+919778553533" },
                  { label: "For Tracking:", value: "+91 97785 53533", href: "tel:+919778553533" },
                  { label: "For Complaint:", value: "+91 97785 53533", href: "tel:+919778553533" },
                ].map(({ label, value, href }) => (
                  <div key={label} className="flex items-center gap-3">
                    <BsChatSquareDotsFill className="text-base flex-shrink-0" style={{ color: "#888" }} />
                    <span className="text-sm" style={{ color: "#444" }}>
                      {label}{" "}
                      <a href={href} className="font-bold" style={{ color: "#222" }}>
                        {value}
                      </a>
                    </span>
                  </div>
                ))}

                {/* Email */}
                <div className="flex items-center gap-3">
                  <FiMail className="text-base flex-shrink-0" style={{ color: "#888" }} />
                  <a
                    href="mailto:sandeep@tripuraagro.com"
                    className="text-sm font-medium"
                    style={{ color: "#444" }}
                  >
                    sandeep@tripuraagro.com
                  </a>
                </div>
              </div>

              {/* Address */}
              <p className="text-sm leading-relaxed" style={{ color: "#555" }}>
                <span className="font-semibold" style={{ color: "#333" }}>Address:</span>{" "}
                1st Floor, Panigrahy Complex, Janata Lane, Digapahandi, Ganjam, Odisha
              </p>
            </div>
          </div>

        </div>
        </div>
      </div>

      {/* ── Map Section ── */}
      <div style={{ background: "#f7f3e3" }} className="pb-14">
        <div className="text-center py-8 px-4">
          <h2 className="font-bold mb-3" style={{ color: "#2d3a1e", fontSize: "1.4rem" }}>
            Find Us on the Map
          </h2>
          <p className="text-sm max-w-lg mx-auto" style={{ color: "#666" }}>
            Discover our space with ease and let your journey lead you to us.
            The map below will guide you to where connections turn into lasting relationships.
          </p>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div style={{ borderRadius: "6px", overflow: "hidden", height: "380px" }}>
            <iframe
              title="Tripura Agro Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3763.8471262624244!2d84.5700540742636!3d19.37577098189147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a3d376d889295f3%3A0x53e064ce4bbeee65!2sPanigrahy%20complex!5e0!3m2!1sen!2sin!4v1782456478410!5m2!1sen!2sin" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

    </div>
  );
}